import { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, ArrowUp, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

const PHONE = '971505213228';

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

/** Renders the answer: markdown links, **bold** and bare catalogue paths. */
const ANSWER_PATTERN = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\/(?:materials|products|compare|brands|forms|insights)\/[a-z0-9/-]+)/g;
const LINK_CLASS = 'underline underline-offset-2 decoration-[var(--px-accent)] hover:text-[var(--px-accent)]';

function Answer({ text }) {
  return (
    <>
      {text.split(ANSWER_PATTERN).map((part, i) => {
        const md = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (md) {
          const [, label, href] = md;
          return href.startsWith('/')
            ? <Link key={i} to={href} className={LINK_CLASS}>{label}</Link>
            : <a key={i} href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>{label}</a>;
        }
        if (/^\*\*[^*]+\*\*$/.test(part)) {
          return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
        }
        if (/^\/(materials|products|compare|brands|forms|insights)\//.test(part)) {
          return <Link key={i} to={part} className={LINK_CLASS}>{part}</Link>;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

export default function SalesDesk() {
  const { t, lang, isRtl } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: '', email: '', phone: '', company: '' });
  const [sendingLead, setSendingLead] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming, showLead]);

  // other pages (the contact page channel tile) open the desk by dispatching this event
  useEffect(() => {
    const openDesk = () => setOpen(true);
    window.addEventListener('axicor:open-desk', openDesk);
    return () => window.removeEventListener('axicor:open-desk', openDesk);
  }, []);

  const starters = [t('desk.starter1'), t('desk.starter2'), t('desk.starter3')];

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || streaming) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: question }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sales-agent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: question, lang }),
      });
      if (!res.ok || !res.body) throw new Error('request failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        for (const chunk of chunks) {
          const line = chunk.replace(/^data: /, '').trim();
          if (!line) continue;
          let payload;
          try { payload = JSON.parse(line); } catch { continue; }
          if (payload.session_id) setSessionId(payload.session_id);
          if (payload.delta) {
            setMessages(prev => {
              const next = [...prev];
              next[next.length - 1] = { role: 'assistant', content: next[next.length - 1].content + payload.delta };
              return next;
            });
          }
          if (payload.error) throw new Error(payload.error);
        }
      }
    } catch {
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', content: t('desk.failed') };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const toWhatsApp = async () => {
    try {
      const res = await api.post('/sales-agent/whatsapp', { session_id: sessionId, message: '', lang });
      window.open(res.data.url, '_blank', 'noopener');
    } catch {
      window.open(`https://wa.me/${PHONE}`, '_blank', 'noopener');
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setSendingLead(true);
    try {
      await api.post('/sales-agent/handoff', { ...lead, session_id: sessionId, lang });
      toast.success(t('desk.handoffDone'));
      setShowLead(false);
      setLead({ name: '', email: '', phone: '', company: '' });
      setMessages(prev => [...prev, { role: 'assistant', content: t('desk.handoffDone') }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('desk.failed'));
    } finally {
      setSendingLead(false);
    }
  };

  const field = 'w-full bg-transparent border-b px-hair py-2 text-sm text-[var(--px-ink)] placeholder:text-[var(--px-ink-soft)] focus:outline-none focus:border-[var(--px-ink)] transition-colors';

  return (
    <div className={`fixed bottom-6 z-50 ${isRtl ? 'left-6' : 'right-6'}`} data-testid="sales-desk">
      {open && (
        <div
          data-testid="sales-desk-panel"
          className="mb-3 w-[calc(100vw-3rem)] sm:w-[400px] h-[min(620px,calc(100vh-7rem))] flex flex-col bg-[var(--px-paper)] border px-hair shadow-2xl rounded-lg overflow-hidden animate-in fade-in slide-in-from-bottom-3 duration-300"
        >
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b px-hair bg-[var(--px-surface)]">
            <div>
              <p className="px-data text-[9px] uppercase tracking-[0.3em] text-[var(--px-ink-soft)]">
                {t('desk.kicker')}
              </p>
              <h3 className="px-display text-base font-semibold text-[var(--px-ink)] mt-1">{t('desk.title')}</h3>
            </div>
            <button onClick={() => setOpen(false)} data-testid="sales-desk-close" aria-label={t('desk.close')}
              className="text-[var(--px-ink-soft)] hover:text-[var(--px-ink)] transition-colors mt-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="sales-desk-messages">
            {messages.length === 0 && (
              <div>
                <p className="text-sm text-[var(--px-ink-soft)] leading-relaxed">{t('desk.intro')}</p>
                <div className="mt-4 space-y-2">
                  {starters.map((s, i) => (
                    <button key={i} onClick={() => send(s)} data-testid={`sales-desk-starter-${i}`}
                      className="w-full text-start text-sm text-[var(--px-ink)] border px-hair rounded-md px-3 py-2 hover:border-[var(--px-ink)] hover:bg-[var(--px-surface)] transition-colors duration-300">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`sales-desk-msg-${m.role}-${i}`}
                className={m.role === 'user' ? 'flex justify-end' : ''}>
                <div className={m.role === 'user'
                  ? 'bg-[var(--px-ink)] text-white text-sm rounded-lg rounded-ee-none px-3.5 py-2.5 max-w-[85%] leading-relaxed'
                  : 'text-sm text-[var(--px-ink)] leading-relaxed whitespace-pre-line'}>
                  {m.role === 'assistant' ? <Answer text={m.content} /> : m.content}
                  {m.role === 'assistant' && !m.content && streaming && (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--px-ink-soft)]" />
                  )}
                </div>
              </div>
            ))}

            {showLead && (
              <form onSubmit={submitLead} data-testid="sales-desk-lead-form"
                className="border px-hair rounded-md p-4 bg-[var(--px-surface)] space-y-3">
                <p className="px-data text-[9px] uppercase tracking-[0.25em] text-[var(--px-ink-soft)]">
                  {t('desk.handoffTitle')}
                </p>
                <input required placeholder={t('desk.name')} value={lead.name} data-testid="sales-desk-lead-name"
                  onChange={e => setLead(p => ({ ...p, name: e.target.value }))} className={field} />
                <input required type="email" placeholder={t('desk.email')} value={lead.email} data-testid="sales-desk-lead-email"
                  onChange={e => setLead(p => ({ ...p, email: e.target.value }))} className={field} />
                <input placeholder={t('desk.company')} value={lead.company} data-testid="sales-desk-lead-company"
                  onChange={e => setLead(p => ({ ...p, company: e.target.value }))} className={field} />
                <button type="submit" disabled={sendingLead} data-testid="sales-desk-lead-submit"
                  className="px-data w-full text-[10px] uppercase tracking-[0.18em] bg-[var(--px-ink)] text-white py-3 rounded-md disabled:opacity-50">
                  {sendingLead ? t('desk.sending') : t('desk.handoffSubmit')}
                </button>
              </form>
            )}
          </div>

          <div className="border-t px-hair px-4 py-3 bg-[var(--px-surface)]">
            <div className="flex items-end gap-2">
              <textarea
                rows={1}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={t('desk.placeholder')}
                data-testid="sales-desk-input"
                className="flex-1 resize-none bg-transparent text-sm text-[var(--px-ink)] placeholder:text-[var(--px-ink-soft)] focus:outline-none max-h-24 py-2"
              />
              <button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="sales-desk-send"
                aria-label={t('desk.send')}
                className="w-9 h-9 rounded-full bg-[var(--px-ink)] text-white flex items-center justify-center disabled:opacity-30 hover:scale-105 transition-transform">
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex items-center gap-4 mt-3">
              <button onClick={toWhatsApp} data-testid="sales-desk-whatsapp"
                className="px-data flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink-soft)] hover:text-[#25D366] transition-colors">
                <WhatsAppIcon className="w-3.5 h-3.5" /> {t('desk.whatsapp')}
              </button>
              <button onClick={() => setShowLead(true)} data-testid="sales-desk-handoff"
                className="px-data text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink-soft)] hover:text-[var(--px-ink)] transition-colors">
                {t('desk.handoff')}
              </button>
            </div>
            <p className="text-[10px] text-[var(--px-ink-soft)] mt-2 leading-relaxed">{t('desk.disclaimer')}</p>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        data-testid="sales-desk-toggle"
        aria-label={open ? t('desk.close') : t('desk.title')}
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-transform duration-300 hover:scale-105 ${
          open ? 'bg-[var(--px-ink-soft)] text-white' : 'bg-[var(--px-ink)] text-white'
        } ${isRtl ? 'ms-auto' : 'ms-auto'}`}
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>
    </div>
  );
}
